import { forwardRef } from 'react';
import {
    useTheme,
    useMediaQuery,
    Box,
    Typography,
    IconButton,
    LinearProgress,
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Slide,
    alpha,
} from '@mui/material';
import {
    Close,
    CheckCircleOutline,
    ErrorOutline,
    WarningAmberOutlined,
    InfoOutlined,
} from '@mui/icons-material';


const Transition = forwardRef((props, ref) => (
    <Slide direction="up" ref={ref} {...props} />
));

const SEVERITY_CONFIG = {
    default: {
        icon: null,
        color: (palette) => palette.primary.main,
    },
    info: {
        icon: InfoOutlined,
        color: (palette) => palette.primary.main,
    },
    success: {
        icon: CheckCircleOutline,
        color: (palette) => palette.green.main,
    },
    warning: {
        icon: WarningAmberOutlined,
        color: (palette) => palette.amber.main,
    },
    error: {
        icon: ErrorOutline,
        color: (palette) => palette.red.main,
    },
};

/**
 * ProgressModal — shared modal component for the entire system.
 *
 * Props:
 *   open             {boolean}          required
 *   onClose          {function}         required
 *   title            {string|ReactNode}
 *   subtitle         {string}           small description below title
 *   icon             {ReactNode}        custom icon (overrides severity icon)
 *   severity         {'default'|'info'|'success'|'warning'|'error'}
 *   children         {ReactNode}        modal content
 *   onConfirm        {function}         if provided → shows Confirm button
 *   confirmText      {string}           default 'OK'
 *   cancelText       {string}           default 'Cancel' (null → hidden)
 *   confirmColor     {string}           override Confirm button color
 *   confirmDisabled  {boolean}
 *   actions          {ReactNode}        replaces default footer buttons entirely
 *   loading          {boolean}          shows progress bar, disables Confirm
 *   progress         {number|null}      null = indeterminate, 0–100 = determinate with %
 *   maxWidth         {'xs'|'sm'|'md'|'lg'|'xl'}  default 'sm'
 *   fullWidth        {boolean}          default true
 *   disableBackdropClose {boolean}      default false
 *   noPadding        {boolean}          remove content padding
 *   hideFooter       {boolean}          hide footer
 */
const ProgressModal = ({
    open,
    onClose,
    title,
    subtitle,
    icon,
    severity = 'default',
    children,
    onConfirm,
    confirmText = 'OK',
    cancelText = 'Cancel',
    confirmColor,
    confirmDisabled = false,
    actions,
    loading = false,
    progress = null,        // null = indeterminate, 0–100 = determinate with %
    maxWidth = 'sm',
    fullWidth = true,
    disableBackdropClose = false,
    noPadding = false,
    hideFooter = false,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const config = SEVERITY_CONFIG[severity] ?? SEVERITY_CONFIG.default;
    const accentColor = config.color(theme.palette);
    const SeverityIcon = config.icon;
    const resolvedIcon = icon ?? (SeverityIcon ? <SeverityIcon /> : null);

    const resolvedConfirmColor = confirmColor ?? (
        severity === 'error' ? 'error' :
        severity === 'warning' ? 'warning' :
        severity === 'success' ? 'success' :
        'primary'
    );

    const handleBackdropClose = (_, reason) => {
        if (disableBackdropClose && reason === 'backdropClick') return;
        onClose?.();
    };

    const titleBarBg = isDark
        ? alpha(theme.palette.background.alt, 0.95)
        : alpha(theme.palette.background.alt, 0.98);

    const paperBg = isDark
        ? theme.palette.background.alt
        : theme.palette.background.default;

    return (
        <Dialog
            open={open}
            onClose={handleBackdropClose}
            maxWidth={maxWidth}
            fullWidth={fullWidth}
            fullScreen={isMobile}
            keepMounted={false}
            slots={{ transition: Transition }}
            slotProps={{
                paper: {
                    elevation: 0,
                    sx: {
                        borderRadius: isMobile ? 0 : 1,
                        background: paperBg,
                        border: `1px solid ${alpha(theme.palette.neutral.main, isDark ? 0.18 : 0.12)}`,
                        boxShadow: isDark
                            ? `0 8px 32px ${alpha('#000', 0.6)}, 0 2px 8px ${alpha('#000', 0.4)}`
                            : `0 20px 60px ${alpha('#000', 0.22)}, 0 6px 20px ${alpha('#000', 0.14)}, 0 1px 4px ${alpha('#000', 0.1)}`,
                        overflow: 'hidden',
                    },
                },
                backdrop: {
                    sx: {
                        backgroundColor: alpha('#000', isDark ? 0.6 : 0.4),
                        backdropFilter: 'blur(4px)',
                    },
                },
            }}
        >
            {/* ── Title bar ─────────────────────────────────────── */}
            <Box
                sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.5,
                    px: 2,
                    pt: 2,
                    pb: 1.5,
                    background: titleBarBg,
                    borderBottom: `1px solid ${alpha(theme.palette.neutral.main, isDark ? 0.15 : 0.1)}`,                    
                }}
            >
                {/* Icon */}
                {resolvedIcon && (
                    <Box
                        sx={{
                            color: accentColor,
                            display: 'flex',
                            alignItems: 'center',
                            mt: 0.25,
                            fontSize: 22,
                            flexShrink: 0,
                            '& .MuiSvgIcon-root': { fontSize: 'inherit' },
                        }}
                    >
                        {resolvedIcon}
                    </Box>
                )}

                {/* Title + subtitle */}
                <Box flex={1} minWidth={0} pr={4} gap={1}>
                    <Typography
                        variant="h6"
                        fontWeight={600}
                        fontSize="0.9375rem"
                        lineHeight={1.35}
                        noWrap
                        title={typeof title === 'string' ? title : undefined}
                    >
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            fontSize="0.78rem"
                            mt={0.25}
                            sx={{ opacity: 0.8 }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>

                {/* Close — Windows X button */}
                <IconButton
                    onClick={onClose}
                    aria-label="close"
                >
                    <Close sx={{ fontSize: 16 }} />
                </IconButton>
            </Box>

            {/* ── Content ───────────────────────────────────────── */}
            <DialogContent sx={{ p: noPadding ? 0 : '20px 24px' }}>
                {loading && (
                    <Box sx={{ mb: children ? 2 : 0 }}>
                        <LinearProgress
                            variant={progress != null ? 'determinate' : 'indeterminate'}
                            value={progress != null ? Math.min(100, Math.max(0, progress)) : undefined}
                            sx={{
                                height: 10,
                                borderRadius: 1,
                                backgroundColor: alpha(accentColor, isDark ? 0.2 : 0.15),
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 1,
                                    backgroundColor: accentColor,
                                },
                            }}
                        />
                        {progress != null && (
                            <Typography
                                variant="caption"
                                sx={{ display: 'block', textAlign: 'right', mt: 0.5, color: 'text.secondary' }}
                            >
                                {Math.round(progress)}%
                            </Typography>
                        )}
                    </Box>
                )}
                {children}
            </DialogContent>

            {/* ── Footer ────────────────────────────────────────── */}
            {!hideFooter && (
                <DialogActions
                    sx={{
                        borderTop: `1px solid ${alpha(theme.palette.neutral.main, isDark ? 0.15 : 0.1)}`,
                        background: titleBarBg,
                        gap: 1,
                    }}
                >
                    {actions ?? (
                        <>
                            {cancelText && (
                                <Button
                                    variant="outlined"
                                    size="small"
                                    onClick={onClose}
                                >
                                    {cancelText}
                                </Button>
                            )}
                            {onConfirm && (
                                <Button
                                    variant="contained"
                                    size="small"
                                    color={resolvedConfirmColor}
                                    onClick={onConfirm}
                                    disabled={loading || confirmDisabled}
                                >
                                    {confirmText}
                                </Button>
                            )}
                        </>
                    )}
                </DialogActions>
            )}
        </Dialog>
    );
};

export default ProgressModal;
